import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast } from '../components/Toast';

interface ToastItem {
  id: string;
  message: string;
  duration: number;
}

interface ToastContextType {
  showToast: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const MAX_TOASTS = 3;

  const showToast = useCallback((message: string, duration = 2000) => {
    const id = `${Date.now()}-${Math.random()}`;
    const newToast: ToastItem = { id, message, duration };
    
    setToasts(prev => {
      const updated = [...prev, newToast];
      // Keep only the last MAX_TOASTS toasts (newest are added to end)
      return updated.slice(-MAX_TOASTS);
    });

    // Auto-remove toast after duration + animation time
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, duration + 300); // Add 300ms for fade-out animation
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed bottom-8 left-0 right-0 z-50 pointer-events-none flex flex-col-reverse items-center gap-2">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} show={true} duration={toast.duration} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

